import { Button } from "@legal-500/legal500-design-system";
import { useMemo, useState } from "react";
import { PracticePicker } from "./PracticePicker.js";
import type { CollectedMapping, PracticeOption } from "./types.js";
import styles from "../PipelineBrowser.module.css";

const MISMATCH_ERROR =
  "Cannot map across hierarchy levels — Source and Destination must both be a table, or both be a practice area (no table).";

const DUPLICATE_SOURCE_ERROR =
  "This source is already mapped to another destination — a source can only be used once.";

// A mapping is only valid if source and destination sit at the same hierarchy
// level (both table rows, or both practice-area-only rows). Returns null once
// both sides are picked and levels match, or before both sides are picked.
function getMismatchError(
  source: PracticeOption | null,
  dest: PracticeOption | null,
): string | null {
  if (source == null || dest == null) return null;

  const sameTableLevel = (source.tableId != null) === (dest.tableId != null);
  // Defensive symmetry check: practiceAreaId presence should agree too.
  const samePracticeAreaLevel = (source.practiceAreaId != null) === (dest.practiceAreaId != null);

  return sameTableLevel && samePracticeAreaLevel ? null : MISMATCH_ERROR;
}

// A source can only be mapped once, even to different destinations, so a chosen
// source is rejected as soon as it already appears in the collected list.
// Destinations may repeat freely — this check is one-directional.
function getDuplicateSourceError(
  mappings: CollectedMapping[],
  source: PracticeOption | null,
): string | null {
  if (source == null) return null;
  const isDuplicate = mappings.some(
    m => m.source.editorialCategoryId === source.editorialCategoryId,
  );
  return isDuplicate ? DUPLICATE_SOURCE_ERROR : null;
}

type OptionsState = {
  options: PracticeOption[];
  loading: boolean;
  error: string | null;
};

type MappingBuilderProps = {
  source: OptionsState;
  dest: OptionsState;
  mappings: CollectedMapping[];
  onAdd: (source: PracticeOption, dest: PracticeOption) => void;
  onRemove: (key: string) => void;
  disabled?: boolean;
};

// Two practice pickers side by side; "Add mapping" appends the chosen pair to the
// parent-owned list, rendered below as a Source → Destination table.
export function MappingBuilder({
  source,
  dest,
  mappings,
  onAdd,
  onRemove,
  disabled,
}: MappingBuilderProps) {
  const [selectedSource, setSelectedSource] = useState<PracticeOption | null>(null);
  const [selectedDest, setSelectedDest] = useState<PracticeOption | null>(null);

  const mismatchError = useMemo(
    () => getMismatchError(selectedSource, selectedDest),
    [selectedSource, selectedDest],
  );

  const duplicateSourceError = useMemo(
    () => getDuplicateSourceError(mappings, selectedSource),
    [mappings, selectedSource],
  );

  // Proactively hide sources that are already mapped, rather than only
  // reactively blocking them via getDuplicateSourceError above.
  const filteredSourceOptions = useMemo(
    () =>
      source.options.filter(
        o => !mappings.some(m => m.source.editorialCategoryId === o.editorialCategoryId),
      ),
    [source.options, mappings],
  );

  // Both checks can be true at once (e.g. a duplicate source re-picked
  // alongside a level-mismatched destination), so join rather than pick one.
  const combinedError = [duplicateSourceError, mismatchError].filter(Boolean).join(" ") || null;

  const canAdd =
    !disabled &&
    selectedSource != null &&
    selectedDest != null &&
    !mismatchError &&
    !duplicateSourceError;

  const handleAdd = () => {
    if (selectedSource == null || selectedDest == null) return;
    onAdd(selectedSource, selectedDest);
    setSelectedSource(null);
    setSelectedDest(null);
  };

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Practice mappings</div>

      <div className={styles.mappingPickers}>
        <PracticePicker
          label="Source practice"
          options={filteredSourceOptions}
          loading={source.loading}
          error={source.error}
          selectedKey={selectedSource?.key ?? null}
          onSelect={setSelectedSource}
          disabled={disabled}
        />
        <PracticePicker
          label="Destination practice"
          options={dest.options}
          loading={dest.loading}
          error={dest.error}
          selectedKey={selectedDest?.key ?? null}
          onSelect={setSelectedDest}
          disabled={disabled}
        />
      </div>

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!canAdd}
          onClick={handleAdd}
        >
          Add mapping
        </Button>
        {combinedError && <span className={styles.mappingError}>{combinedError}</span>}
      </div>

      {mappings.length > 0 && (
        <div className={styles.tableContainer}>
          <table className={styles.crossRefTable}>
            <thead>
              <tr>
                <th>Source</th>
                <th>Destination</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {mappings.map(m => (
                <tr key={m.key}>
                  <td>{m.source.label}</td>
                  <td>{m.dest.label}</td>
                  <td>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={disabled}
                      onClick={() => onRemove(m.key)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
